(() => {
  const vertex = 'attribute vec2 position; void main(){gl_Position=vec4(position,0.0,1.0);}';
  // Ray/projective mapping preserves the curved photographs and flat tilt of the approved film.
  const fragment = `precision highp float;
    uniform sampler2D atlas;
    uniform vec2 resolution;
    uniform float viewWidth;
    uniform float elapsed;
    uniform float orbit;
    const float PI=3.141592653589793, TAU=6.283185307179586, STEP=TAU/16.0;
    float ease(float p){return 1.0-pow(1.0-clamp(p,0.0,1.0),4.0);}
    float smooth(float p){p=clamp(p,0.0,1.0);return p*p*(3.0-2.0*p);}
    float entryRank(float i){
      if(i<4.5)return max(0.0,7.0-2.0*i);
      if(i<12.0)return 2.0*(i-4.0);
      return 39.0-2.0*i;
    }
    void main(){
      float formed=ease(elapsed/1.85), growth=mix(0.74,1.0,formed), radius=640.0*growth;
      float spin=orbit;
      float se=sin(0.28),ce=cos(0.28),F=2800.0;
      float ry=((1.0-gl_FragCoord.y/resolution.y)*780.0-320.0)/F;
      float dx=((gl_FragCoord.x/resolution.x-0.5)*viewWidth)/F;
      float dy=ry*ce+se,dz=ry*se-ce,az=F*ce,ay=-F*se;
      float a=dx*dx+dz*dz,b=2.0*az*dz,disc=b*b-4.0*a*(az*az-radius*radius);
      if(disc<=0.0){gl_FragColor=vec4(0.0);return;}
      float q=sqrt(disc); vec4 color=vec4(0.0);
      for(int side=0;side<2;side++){
        float t=(-b+(side==0?q:-q))/(2.0*a);
        float x=t*dx,z=az+t*dz;
        float angle=mod(atan(x,z)-spin+STEP/2.0,TAU);
        float index=floor(angle/STEP), delta=mod(angle,STEP)-STEP/2.0;
        float p=clamp((elapsed-0.035-entryRank(index)*0.085)/0.82,0.0,1.0);
        float g=ease(p),opacity=smooth(1.0-pow(1.0-p,3.0));
        float span=0.32*mix(0.84,1.0,g),cardHeight=340.0*growth*mix(0.84,1.0,g);
        float v=(ay+t*dy-48.0*(1.0-g)*growth)/cardHeight+0.5;
        vec2 uv=vec2(delta/span+0.5,v);
        if(abs(delta)>span/2.0||v<0.0||v>1.0||opacity<=0.0)continue;
        // Rounded corners stay in the texture plane, including on the far side.
        vec2 pixel=uv*vec2(512.0,850.0),halfSize=vec2(256.0,425.0);
        vec2 corner=abs(pixel-halfSize)-(halfSize-vec2(24.0));
        float distance=length(max(corner,0.0))+min(max(corner.x,corner.y),0.0)-24.0;
        float aa=max(1.0,2.0*1080.0/resolution.x);
        float alpha=(1.0-smoothstep(-aa,aa,distance))*opacity;
        vec2 cell=vec2(mod(index,4.0),floor(index/4.0));
        vec2 tex=(cell*vec2(516.0,854.0)+vec2(2.5)+uv*vec2(511.0,849.0))/vec2(2064.0,3416.0);
        vec3 photo=texture2D(atlas,tex).rgb;
        float light=0.30+0.70*smooth((z/radius+1.0)/2.0);
        color=vec4(photo*light*alpha,alpha)+color*(1.0-alpha);
      }
      gl_FragColor=color;
    }`;
  function mount(root) {
    const ring=root.querySelector('[data-operators-ring]');
    if(!ring||ring.dataset.initialized)return;
    ring.dataset.initialized='true';
    const canvas=ring.querySelector('canvas'),control=root.querySelector('[data-operators-motion]');
    const reduced=matchMedia('(prefers-reduced-motion: reduce)');
    let gl,program,texture,frame=0,last=0,elapsed=0,ready=false,visible=false,paused=false,destroyed=false,loading=false;
    let resolution,timeLocation,widthLocation,orbitLocation;
    const speed=0.25*5000/Math.max(3000,Number(root.dataset.autoplay)||5000);
    const remove=[];
    const listen=(node,event,fn,options)=>{if(!node)return;node.addEventListener(event,fn,options);remove.push(()=>node.removeEventListener(event,fn,options));};
    function draw(time){
      if(!ready||!gl||gl.isContextLost())return;
      gl.uniform2f(resolution,canvas.width,canvas.height);gl.uniform1f(widthLocation,ring.clientWidth/ring.clientHeight*780);gl.uniform1f(timeLocation,Math.min(time,3));gl.uniform1f(orbitLocation,(speed*(time-2.4)-0.8*Math.pow(1-Math.min(time/2.1,1),4))%(Math.PI*2));gl.drawArrays(gl.TRIANGLES,0,6);
      ring.dataset.ready='true';
    }
    function resize(){
      const rect=ring.getBoundingClientRect();
      const width=Math.min(1620,Math.max(540,Math.round(rect.width*Math.min(devicePixelRatio||1,1.5))));
      const height=Math.round(width*rect.height/rect.width);
      if(canvas.width!==width||canvas.height!==height){canvas.width=width;canvas.height=height;}
      if(gl){gl.viewport(0,0,width,height);draw(reduced.matches?2.4:elapsed);}
    }
    function tick(now){
      frame=0;
      if(destroyed||!visible||document.hidden||paused||reduced.matches||!ready)return;
      if(!last)last=now;
      elapsed+=Math.min(0.05,(now-last)/1000);last=now;
      draw(elapsed);frame=requestAnimationFrame(tick);
    }
    function sync(){
      cancelAnimationFrame(frame);frame=0;last=0;
      const playing=ready&&visible&&!document.hidden&&!paused&&!reduced.matches&&!destroyed;
      root.dataset.archiveMotion=String(playing);
      if(control){control.hidden=!ready||reduced.matches;control.setAttribute('aria-pressed',String(paused));control.setAttribute('aria-label',paused?control.dataset.playLabel:control.dataset.pauseLabel);}
      if(ready&&reduced.matches)draw(2.4);
      else if(playing)frame=requestAnimationFrame(tick);
    }
    function compile(type,source){const shader=gl.createShader(type);gl.shaderSource(shader,source);gl.compileShader(shader);if(!gl.getShaderParameter(shader,gl.COMPILE_STATUS)){const error=gl.getShaderInfoLog(shader);gl.deleteShader(shader);throw Error(error);}return shader;}
    function setup(image){
      if(destroyed)return;
      try{
        gl=canvas.getContext('webgl',{alpha:true,antialias:false,premultipliedAlpha:true,depth:false,stencil:false,powerPreference:'low-power'});
        if(!gl)return;
        const vs=compile(gl.VERTEX_SHADER,vertex),fs=compile(gl.FRAGMENT_SHADER,fragment);
        program=gl.createProgram();gl.attachShader(program,vs);gl.attachShader(program,fs);gl.linkProgram(program);
        gl.deleteShader(vs);gl.deleteShader(fs);
        if(!gl.getProgramParameter(program,gl.LINK_STATUS))throw Error(gl.getProgramInfoLog(program));
        gl.useProgram(program);
        const buffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,buffer);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]),gl.STATIC_DRAW);
        const position=gl.getAttribLocation(program,'position');gl.enableVertexAttribArray(position);gl.vertexAttribPointer(position,2,gl.FLOAT,false,0,0);
        texture=gl.createTexture();gl.bindTexture(gl.TEXTURE_2D,texture);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);gl.texImage2D(gl.TEXTURE_2D,0,gl.RGB,gl.RGB,gl.UNSIGNED_BYTE,image);
        gl.uniform1i(gl.getUniformLocation(program,'atlas'),0);resolution=gl.getUniformLocation(program,'resolution');timeLocation=gl.getUniformLocation(program,'elapsed');widthLocation=gl.getUniformLocation(program,'viewWidth');orbitLocation=gl.getUniformLocation(program,'orbit');
        ready=true;resize();sync();
      }catch(error){ready=false;delete ring.dataset.ready;root.dataset.ringFallback='true';if(control)control.hidden=true;}
    }
    function load(){
      if(loading||destroyed)return;loading=true;
      const image=new Image();image.crossOrigin='anonymous';image.decoding='async';
      image.onload=()=>setup(image);image.onerror=()=>{root.dataset.ringFallback='true';};image.src=ring.dataset.atlas;
      remove.push(()=>{image.onload=null;image.onerror=null;});
    }
    const approach=new IntersectionObserver(([entry])=>{if(entry.isIntersecting){load();approach.disconnect();}},{rootMargin:'500px'});
    approach.observe(root);
    const view=new IntersectionObserver(([entry])=>{visible=entry.isIntersecting;sync();},{threshold:0});view.observe(ring);
    const size=new ResizeObserver(resize);size.observe(ring);
    listen(document,'visibilitychange',sync);listen(reduced,'change',sync);
    listen(control,'click',()=>{paused=!paused;sync();});
    listen(canvas,'webglcontextlost',event=>{event.preventDefault();ready=false;delete ring.dataset.ready;sync();});
    listen(canvas,'webglcontextrestored',()=>{ready=false;loading=false;elapsed=2.4;load();});
    const dialog=root.querySelector('[data-operators-dialog]');
    listen(root.querySelector('[data-operators-open]'),'click',()=>{if(dialog&&!dialog.open){dialog.showModal();document.documentElement.classList.add('bc-operators-story-open');}});
    listen(root.querySelector('[data-operators-close]'),'click',()=>dialog?.close());
    listen(dialog,'close',()=>document.documentElement.classList.remove('bc-operators-story-open'));
    listen(dialog,'click',event=>{if(event.target!==dialog)return;const rect=dialog.getBoundingClientRect();if(event.clientX<rect.left||event.clientX>rect.right||event.clientY<rect.top||event.clientY>rect.bottom)dialog.close();});
    const unload=event=>{
      if(!event.target.contains(root))return;destroyed=true;cancelAnimationFrame(frame);approach.disconnect();view.disconnect();size.disconnect();remove.forEach(fn=>fn());
      if(gl){gl.deleteTexture(texture);gl.deleteProgram(program);gl.getExtension('WEBGL_lose_context')?.loseContext();}
      if(dialog?.open)dialog.close();document.documentElement.classList.remove('bc-operators-story-open');
    };
    listen(document,'shopify:section:unload',unload);
  }
  const init=(scope=document)=>scope.querySelectorAll('[data-bc-operators]').forEach(mount);
  init();document.addEventListener('shopify:section:load',event=>init(event.target));
})();
